/* ============================================================================
   utility-dock.js — <sf-dock> (sections/utility-dock.liquid) · Sneak Freak · AphCom v1.0.0
   ----------------------------------------------------------------------------
   Enhances the mobile dock rendered by Liquid:
     · fills the "Saved" count bubble from theme.favourites.count() and keeps it in
       sync on `favourites:change` (the bag bubble is theme.js's own <sf-cart-count>);
     · marks the active item from location.pathname when Liquid could not (e.g. the
       favourites page reached through a localized URL prefix).
   Vanilla ES2020, no dependencies beyond window.theme (theme.js, loaded first).
   Custom element guarded with customElements.get; init in connectedCallback,
   teardown in disconnectedCallback (AbortController + pubsub unsubscribe).
   ============================================================================ */
(function () {
  'use strict';

  if (customElements.get('sf-dock')) return;

  class SFDock extends HTMLElement {
    connectedCallback() {
      this._ctrl = new AbortController();
      this._unsubs = [];
      this._bubble = this.querySelector('[data-dock-favourites-count]');

      const boot = () => {
        this._syncFavourites();
        this._markActive();
        const t = window.theme;
        if (t && t.pubsub && typeof t.pubsub.subscribe === 'function') {
          this._unsubs.push(t.pubsub.subscribe('favourites:change', () => this._syncFavourites()));
        }
      };

      // theme.js is deferred and precedes this file, so window.theme.favourites normally exists;
      // fall back to theme:ready (pubsub) or a storage listener so a slow load never leaves a stale "0".
      if (window.theme && window.theme.favourites) {
        boot();
      } else if (window.theme && window.theme.pubsub) {
        this._unsubs.push(window.theme.pubsub.subscribe('theme:ready', boot));
      } else {
        window.addEventListener('DOMContentLoaded', boot, { once: true, signal: this._ctrl.signal });
      }

      // Cross-tab updates: theme.storage writes 'sf:' prefixed keys; any change re-reads the count.
      window.addEventListener(
        'storage',
        (e) => {
          if (!e.key || e.key.indexOf('sf:') === 0) this._syncFavourites();
        },
        { signal: this._ctrl.signal }
      );
    }

    disconnectedCallback() {
      if (this._ctrl) this._ctrl.abort();
      (this._unsubs || []).forEach((fn) => {
        try {
          fn();
        } catch (_) {
          /* noop */
        }
      });
      this._unsubs = [];
    }

    /** Read the favourites count and paint the bubble (hidden at 0). */
    _syncFavourites() {
      if (!this._bubble) return;
      const t = window.theme;
      let n = 0;
      try {
        n = t && t.favourites && typeof t.favourites.count === 'function' ? Number(t.favourites.count()) || 0 : 0;
      } catch (_) {
        n = 0;
      }
      this._bubble.textContent = n > 99 ? '99+' : String(n);
      this._bubble.dataset.count = String(n);
      this._bubble.hidden = n === 0;
    }

    /** Liquid sets .is-active for known templates; this covers paths it cannot see. */
    _markActive() {
      if (this.querySelector('.ubar__item.is-active')) return;
      const here = normalise(location.pathname);
      if (!here || here === '/') return;
      /* the chat button is an action, never "the page you are on" */
      const items = Array.from(this.querySelectorAll('a.ubar__item[href]:not(.ubar__ai)'));
      const query = new URLSearchParams(location.search);
      const match = items.find((a) => {
        const path = normalise(a.getAttribute('href'));
        if (!path || path === '/' || !(here === path || here.indexOf(path + '/') === 0)) return false;
        /* a link that carries a query (Saved's fallback, /collections/all?view=favourites) is only the current
           page with that query — plain /collections/all is not Saved */
        const want = new URL(a.getAttribute('href'), location.origin).searchParams;
        return Array.from(want).every(([k, v]) => query.get(k) === v);
      });
      if (match) {
        match.classList.add('is-active');
        match.setAttribute('aria-current', 'page');
      }
    }
  }

  function normalise(href) {
    if (!href) return '';
    try {
      const url = new URL(href, location.origin);
      return url.pathname.replace(/\/+$/, '') || '/';
    } catch (_) {
      return '';
    }
  }

  customElements.define('sf-dock', SFDock);
})();

/* --- dock height + inert-while-modal (BUILD r) --- */
(function () {
  'use strict';
  const root = document.documentElement;
  const dock = () => document.querySelector('.ubar');

  /* How much of the bottom of the viewport the dock actually eats, measured rather than assumed, so a
     different dock height, a safe-area inset or an orientation change all stay correct. */
  function measure() {
    const el = dock();
    if (!el || getComputedStyle(el).display === 'none') {
      root.style.setProperty('--sf-dock-h', '0px');
      return;
    }
    /* Layout, not the painted rectangle: the dock is translated out of the way when the footer arrives, and a
       transformed getBoundingClientRect would report the gap it leaves as zero height. */
    root.style.setProperty('--sf-dock-h', barHeight(el) + 'px');
  }

  /* the space a fixed bottom bar occupies, from layout: its own height plus its bottom offset */
  function barHeight(el) {
    if (!el) return 0;
    const cs = getComputedStyle(el);
    if (cs.display === 'none' || cs.visibility === 'hidden') return 0;
    const bottom = parseFloat(cs.bottom);
    return Math.max(0, Math.round(el.offsetHeight + (isNaN(bottom) ? 0 : bottom)));
  }

  /* ---- yielding to the footer -------------------------------------------------------------------------
     The dock and the product buy bar both sit against the bottom of the screen, so both would sit on top of
     the footer — including the AphCom credit — once the shopper reaches it. Rather than reserving a
     permanent empty strip under the footer, they slide away at the moment the footer would first be covered
     and slide back as soon as it is not.
     BUILD bp: the footer group's 44px WhatsApp band sits directly above .footer, so it passed under the dock for
     the ~130px of scrolling before the footer reached the clearance line (the bag page, every page). The bars now
     also yield as soon as the band enters the screen (its own observer, no clearance margin). One flag per target:
     an observer's callback lists only the targets that changed, so `some(isIntersecting)` across two targets
     would drop the one that did not. */
  function watchFooter() {
    const footer = document.querySelector('.footer');
    if (!footer || !('IntersectionObserver' in window)) return;
    const band = document.querySelector('.whatsapp-band');
    let io = null, bandIo = null;
    const seen = { footer: false, band: false };
    const sync = () => root.classList.toggle('footer-in-view', seen.footer || seen.band);
    const last = (entries) => entries[entries.length - 1].isIntersecting;
    const build = () => {
      if (io) { io.disconnect(); io = null; }
      if (bandIo) { bandIo.disconnect(); bandIo = null; }
      seen.footer = seen.band = false;
      const scrollable = document.documentElement.scrollHeight > window.innerHeight + 80;
      if (!scrollable) { root.classList.remove('footer-in-view'); return; }
      const bars = [dock(), document.querySelector('.pdp-buybar')];
      const clear = Math.max(0, ...bars.map(barHeight)) + 12;
      io = new IntersectionObserver((entries) => {
        seen.footer = last(entries);
        sync();
      }, { rootMargin: '0px 0px -' + clear + 'px 0px', threshold: 0 });
      io.observe(footer);
      if (band) {
        bandIo = new IntersectionObserver((entries) => {
          seen.band = last(entries);
          sync();
        }, { threshold: 0 });
        bandIo.observe(band);
      }
    };
    build();
    window.addEventListener('resize', build);
    window.addEventListener('orientationchange', build);
    /* the buy bar appears only after the shopper scrolls past the buy row, which changes the clearance */
    const bar = document.querySelector('sf-sticky-buy');
    if (bar && 'MutationObserver' in window) new MutationObserver(build).observe(bar, { attributes: true, attributeFilter: ['hidden', 'class'] });
  }

  let raf = 0;
  const schedule = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(measure); };

  function start() {
    measure();
    watchFooter();
    window.addEventListener('resize', schedule);
    window.addEventListener('orientationchange', schedule);
    if (window.visualViewport) window.visualViewport.addEventListener('resize', schedule);
    const el = dock();
    if (el && 'ResizeObserver' in window) new ResizeObserver(schedule).observe(el);

    /* Shopify Inbox: the centre button opens it (data-shopify-chat-prompt), so it reports Inbox's open state.
       The button is looked up on every change — the theme editor re-renders the dock section. */
    const chat = document.querySelector('shopify-chat');
    if (chat && 'MutationObserver' in window) {
      const syncChat = () => {
        const ai = document.querySelector('.ubar__ai[data-shopify-chat-prompt]');
        if (ai) ai.setAttribute('aria-expanded', String(chat.hasAttribute('open')));
      };
      syncChat();
      new MutationObserver(syncChat).observe(chat, { attributes: true, attributeFilter: ['open'] });
    }

    /* A modal is up: the dock stays visible by design, so make it non-interactive and hide it from the
       accessibility tree instead of leaving a second tab stop outside the trap. */
    const theme = window.theme;
    if (theme && theme.pubsub) {
      let open = 0;
      const apply = () => {
        const d = dock();
        if (!d) return;
        const on = open > 0;
        d.classList.toggle('is-inert', on);
        if (on) d.setAttribute('aria-hidden', 'true'); else d.removeAttribute('aria-hidden');
        d.querySelectorAll('a, button').forEach((n) => { n.tabIndex = on ? -1 : 0; });
      };
      theme.pubsub.subscribe('drawer:open', () => { open += 1; apply(); });
      theme.pubsub.subscribe('drawer:close', () => { open = Math.max(0, open - 1); apply(); });
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
